import { pushLeadEvent, pushBookingEvent } from "@/lib/gtmDataLayer";
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, CalendarCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { parseCalMessage } from "@/lib/calEmbedMessages";

interface StepBookingProps {
  url: string;
  onBack: () => void;
  variant?: "default" | "landing";
  onBooked?: (booking: { uid?: string; startTime?: string }) => void;
  hideEditDetails?: boolean;
}

const StepBooking = ({
  url,
  onBack,
  variant = "default",
  onBooked,
  hideEditDetails = false,
}: StepBookingProps) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(700);
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const bookedRef = useRef(false);

  useEffect(() => {
    if (variant === "landing") {
      const card = document.getElementById("book");
      if (card)
        window.scrollTo({
          top: card.getBoundingClientRect().top + window.scrollY - 12,
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
            .matches
            ? "auto"
            : "smooth",
        });
      return;
    }
    containerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [variant]);

  useEffect(() => {
    if (variant !== "landing") return;
    const handler = (event: MessageEvent) => {
      const parsed = parseCalMessage(
        event.origin,
        event.source === iframeRef.current?.contentWindow,
        event.data,
      );
      if (!parsed) return;
      if (parsed.type === "height") setIframeHeight(parsed.height);
      const motion: ScrollBehavior = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches
        ? "auto"
        : "smooth";
      if (parsed.type === "route") {
        const rect = iframeRef.current?.getBoundingClientRect();
        if (rect && rect.top < 0 && Math.abs(rect.top / rect.height) >= 0.25)
          containerRef.current?.scrollIntoView({
            behavior: motion,
            block: "start",
          });
      }
      if (parsed.type === "scroll") {
        const rect = iframeRef.current?.getBoundingClientRect();
        if (rect) {
          const y = rect.top + parsed.distance;
          if (y < 0 || y > window.innerHeight * 0.6)
            window.scrollBy({ top: y - 16, behavior: motion });
        }
      }
      if (parsed.type === "booked" && !bookedRef.current) {
        bookedRef.current = true;
        void pushBookingEvent();
        onBooked?.(parsed.booking);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [onBooked, variant]);

  if (variant === "landing") {
    return (
      <div ref={containerRef} className="space-y-4">
        {!hideEditDetails && (
          <div className="flex items-center justify-end px-5 sm:px-0">
            <Button
              type="button"
              variant="ghost"
              onClick={onBack}
              className="h-11 text-landing-light-body hover:bg-landing-soft hover:text-landing-ink"
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              Edit my details
            </Button>
          </div>
        )}
        <div className="relative min-h-[500px] overflow-hidden border-y border-landing-input bg-landing-light sm:rounded-xl sm:border">
          {!iframeLoaded && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-landing-light">
              <Loader2 className="h-8 w-8 animate-spin text-landing-red" />
              <p className="text-sm text-landing-light-muted">
                Loading calendar...
              </p>
            </div>
          )}
          <iframe
            ref={iframeRef}
            src={url}
            onLoad={() => setIframeLoaded(true)}
            title="Book your free call"
            className={`w-full border-0 transition-opacity duration-300 ${iframeLoaded ? "opacity-100" : "opacity-0"}`}
            style={{ height: `${iframeHeight}px` }}
            allow="payment"
          />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="space-y-4"
    >
      {/* Section header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="h-5 w-1 rounded-full bg-emerald-500" />
            <CalendarCheck className="h-4 w-4 text-emerald-600" />
            <h3 className="text-xs font-bold uppercase tracking-widest text-emerald-600">
              Book Your Time
            </h3>
          </div>
          <p className="text-slate-500 text-sm ml-3">
            Pick a date and time that works best for you.
          </p>
        </div>
        <Button
          type="button"
          onClick={onBack}
          className="bg-gradient-to-r from-lime-400 to-green-500 hover:from-lime-500 hover:to-green-600 text-white font-bold px-6 h-12 rounded-xl shadow-lg shadow-lime-500/20 hover:shadow-lime-500/30 transition-all duration-200"
        >
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back
        </Button>
      </div>

      {/* Calendar embed */}
      <div className="relative rounded-2xl border border-slate-200 overflow-hidden bg-white min-h-[500px]">
        <AnimatePresence>
          {!iframeLoaded && (
            <motion.div
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white z-10"
            >
              <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
              <p className="text-sm text-slate-400">Loading calendar…</p>
            </motion.div>
          )}
        </AnimatePresence>

        <iframe
          src={url}
          onLoad={() => setIframeLoaded(true)}
          title="Book your consultation"
          className={`w-full border-0 transition-opacity duration-300 ${
            iframeLoaded ? "opacity-100" : "opacity-0"
          }`}
          style={{ height: "700px" }}
          allow="payment"
        />
      </div>
    </motion.div>
  );
};

export default StepBooking;
